import { z } from "zod"

const escapeRegExp = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

// Define special characters that are not allowed, with exceptions for certain fields
const baseSpecialCharacters = '!@#$%^&*()?":{}|<>[]'

// Define error messages
const poBoxNotAllowedMessage = "PO Boxes are not allowed"
const specialCharactersNotAllowedMessage = "Special characters are not allowed"
const japaneseCharactersNotAllowedMessage = "Japanese characters are not allowed"
const chineseCharactersNotAllowedMessage = "Chinese characters are not allowed"
const persianCharactersNotAllowedMessage = "Persian characters are not allowed"

const validateNoPOBox = (value: string) => {
    return !/P\.?\s*O\.?\s*B(?:OX)?.*|POST\s*OFFICE\s*BOX|POBOX|P\.O\.BOX|P\.O\. BOX/i.test(
        value
    )
}

const validateNoSpecialCharacters = (value: string, additionalAllowed = "") => {
    const allowedCharacters = escapeRegExp(additionalAllowed)
    const specialCharacters = baseSpecialCharacters
        .split("")
        .filter(ch => !allowedCharacters.includes(ch))
        .join("")
    return !new RegExp(`[${escapeRegExp(specialCharacters)}]`).test(value)
}

const validateNoJapaneseCharacters = (value: string) => {
    return !/[\u3000-\u303F\u3040-\u309F\u30A0-\u30FF\uFF00-\uFFEF\u4E00-\u9FAF\u3400-\u4DBF]/.test(
        value
    )
}

const validateNoChineseCharacters = (value: string) => {
    return !/[\u4e00-\u9fff\u3400-\u4dbf\u3000-\u303f\uff00-\uffef]/.test(value)
}

const validateNoPersianCharacters = (value: string) => {
    return !/[\u0600-\u06FF\u0750-\u077F\uFB50-\uFDFF\uFE70-\uFEFF\u08A0-\u08FF]/.test(
        value
    )
}

// Field validators
const textField = (
    maxLength: number,
    fieldName: string,
    additionalAllowedChars = "",
    allowPOBox = true
) =>
    z
        .string()
        .trim()
        .min(1, `${fieldName} is required`)
        .max(maxLength, `${fieldName} must be max ${maxLength} characters`)
        .refine(value => allowPOBox || validateNoPOBox(value), {
            message: poBoxNotAllowedMessage
        })
        .refine(value => validateNoSpecialCharacters(value, additionalAllowedChars), {
            message: specialCharactersNotAllowedMessage
        })
        .refine(validateNoJapaneseCharacters, {
            message: japaneseCharactersNotAllowedMessage
        })
        .refine(validateNoChineseCharacters, {
            message: chineseCharactersNotAllowedMessage
        })
        .refine(validateNoPersianCharacters, {
            message: persianCharactersNotAllowedMessage
        })

/////////////////////////////////////////////////
// Phone validation schema
/////////////////////////////////////////////////

const phoneNumberSchema = z
    .string({
        required_error: "Phone number is required"
    })
    .refine(
        value => {
            // Basic pattern to validate phone number format, modify as needed
            const phoneNumberPattern =
                /^[+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,4}[-\s\.]?[0-9]{1,6}$/im
            return phoneNumberPattern.test(value)
        },
        { message: "Invalid phone number format" }
    )

/////////////////////////////////////////////////
// Email validation schema
/////////////////////////////////////////////////

const emailSchema = z
    .string()
    .trim()
    .min(2, { message: "Must be at least 2 characters" })
    .email({
        message: "Invalid email format."
    })

const stateSchema = z.string().trim().min(1, {
    message: "State is required"
})

const shippingAddressSchema = z.object({
    country: textField(10, "Country"),
    city: textField(60, "City"),
    address1: textField(250, "Address", ".,-/", false), // Allow ., , - /, disallow PO Box
    zipCode: textField(10, "ZIP"),
    email: emailSchema,
    firstName: textField(50, "First name"),
    lastName: textField(50, "Last name"),
    phoneNumber: phoneNumberSchema // Mandatory phone number
})

const billingAddressSchema = z.object({
    country: textField(10, "Country"),
    email: emailSchema,
    city: textField(60, "City"),
    address1: textField(250, "Address", ".,-/"), // Allow ., , - /, allow PO Box
    zipCode: textField(10, "ZIP"),
    firstName: textField(50, "First name"),
    lastName: textField(50, "Last name")
})

const shippingAddressWithStateSchema = shippingAddressSchema.extend({
    state: stateSchema
})

const billingAddressWithStateSchema = billingAddressSchema.extend({
    state: stateSchema
})

export const formSchema = (differentShippingAddress: boolean | undefined) =>
    z
        .object({
            shippingAddress: shippingAddressSchema,
            billingAddress: differentShippingAddress
                ? billingAddressSchema
                : z.object({}).optional()
        })
        .refine(data => data.shippingAddress.country !== "ForbiddenCountry", {
            message: "Shipments to this country are not allowed.",
            path: ["shippingAddress", "country"]
        })

export const formSchemaWithShippingState = z
    .object({
        shippingAddress: shippingAddressWithStateSchema
    })
    .refine(data => data.shippingAddress.country !== "ForbiddenCountry", {
        message: "Shipments to this country are not allowed.",
        path: ["shippingAddress", "country"]
    })

export const formSchemaWithBillingState = z.object({
    billingAddress: billingAddressWithStateSchema
})

export const formSchemaWithShippingAndBillingState = (
    differentShippingAddress: boolean | undefined
) =>
    z
        .object({
            shippingAddress: shippingAddressWithStateSchema,
            billingAddress: differentShippingAddress
                ? billingAddressWithStateSchema
                : z.object({}).optional()
        })
        .refine(data => data.shippingAddress.country !== "ForbiddenCountry", {
            message: "Shipments to this country are not allowed.",
            path: ["shippingAddress", "country"]
        })
